import { Col } from 'components/Col';
import { CustomButton } from 'components/CustomButton';
import { CustomTextArea } from 'components/CustomTextArea';
import { GenericModal } from 'components/GenericModal';
import CheckIcon from 'components/Icons/CheckIcon';
import Dislike from 'components/Icons/DislikeIcon';
import RefreshIcon from 'components/Icons/RefreshIcon';
import { Row } from 'components/Row';
import { EtapaProjeto } from 'models/projeto';
import { ChangeEvent, Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { closeModal } from 'store/modules/ModalActions';
import languageValues from 'utils/LanguageUtils';

interface Props {
  variant: 'default' | 'error';
  title: string;
  modalKey: string;
  etapa: EtapaProjeto;
  flgInfantil: boolean;
  onSubmit: (justificativa: string) => Promise<void>;
}

export default function FinalizarEtapaModal(props: Props) {
  const [sent, setSent] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [justificativa, setJustificativa] = useState<string>('');
  const d = useDispatch();

  const icon = useMemo(() => {
    if (loading) return <RefreshIcon currentColor='black' />;
    return props.variant === 'default' ? <CheckIcon /> : <Dislike />;
  }, [loading, props.variant]);

  const submit = useCallback(() => {
    setSent(true);
    if (props.variant === 'error' && !justificativa) return;

    setLoading(true);
    props
      .onSubmit(justificativa)
      .then(() => {
        d(closeModal(props.modalKey));
      })
      .catch(() => {
        setLoading(false);
      });
  }, [d, justificativa, props]);

  const etapaName = useMemo(() => {
    const name = languageValues.pages.projetos.etapas[props.etapa].name;
    return typeof name === 'function' ? name(props.flgInfantil) : name;
  }, [props.etapa, props.flgInfantil]);

  return (
    <GenericModal
      onClose={() => {
        d(closeModal(props.modalKey));
      }}
      title={
        <Fragment>
          {props.title}
          <br />
          <small>{etapaName}</small>
        </Fragment>
      }
      footerContent={
        <Row>
          <Col flex end>
            <CustomButton
              theme={props.variant}
              label={
                languageValues.pages.projetos.actions[
                  props.variant === 'default' ? 'approve' : 'attentionPoint'
                ]
              }
              disabled={
                loading ||
                (props.variant === 'error' &&
                  (!justificativa || !justificativa.length))
              }
              onClick={submit}
              rightIcon={icon}
            />
          </Col>
        </Row>
      }
    >
      <Row>
        <Col>
          <CustomTextArea
            label={languageValues.inputs.observacoes.label}
            placeholder={languageValues.inputs.observacoes.placeholder}
            value={justificativa}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setJustificativa(event.target.value);
            }}
            error={
              sent &&
              props.variant === 'error' &&
              (!justificativa || !justificativa.length)
            }
            helperText={
              sent &&
              props.variant === 'error' &&
              (!justificativa || !justificativa.length)
                ? languageValues.inputs.justificativa.required
                : ''
            }
            counter={true}
            maxSize={2000}
            minRows={1}
            maxRows={4}
          />
        </Col>
      </Row>
    </GenericModal>
  );
}
